import { Slider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useSlider } from '~/hooks/useSlider';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import {
    currenPositionAutoScrollState,
    isAutoScrollingState,
} from '~/state/atoms/viewerAtoms';

/**
 * Scrub slider over the selected structure's extent along the sweep axis. It
 * follows the auto-scroll position while a sweep runs, and a drag stops the
 * sweep and moves the slice to the dragged value. Disabled while there is
 * nothing to scrub through.
 */
export const MovieReviewSlider: React.FC = () => {
    const intl = useIntl();
    const selectedROI = useRecoilValue(selectedROIState);
    const {
        enabled,
        sliderValue,
        handleChange,
        minPosition,
        maxPosition,
        valueAlongAxis,
    } = useSlider(selectedROI);
    const currentPosition = useRecoilValue(currenPositionAutoScrollState);
    const [shouldScroll, setShouldScroll] =
        useRecoilState(isAutoScrollingState);
    const [isUserInteracting, setIsUserInteracting] = useState(false);

    // Follow the sweep while it runs and the user is not dragging.
    useEffect(() => {
        if (!isUserInteracting && shouldScroll && currentPosition) {
            handleChange({}, valueAlongAxis(currentPosition));
        }
    }, [
        currentPosition,
        shouldScroll,
        isUserInteracting,
        handleChange,
        valueAlongAxis,
    ]);

    const handleSliderChange = (
        _event: unknown,
        newValue: number | number[]
    ) => {
        // A drag takes over from the sweep.
        setIsUserInteracting(true);
        setShouldScroll(false);
        handleChange(_event, newValue);
    };

    const handleSliderChangeCommitted = () => {
        setIsUserInteracting(false);
    };

    const displayedValue =
        shouldScroll && currentPosition
            ? valueAlongAxis(currentPosition)
            : sliderValue;

    return (
        <Slider
            value={displayedValue}
            min={minPosition}
            max={maxPosition}
            step={1}
            disabled={!enabled}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            aria-label={intl.formatMessage({ id: 'review.player.slider' })}
        />
    );
};
